import { css } from '@emotion/react';
import { math } from 'polished';
import { BorderBottomRadius, BorderRadius, BorderTopRadius, BorderRightRadius, BorderLeftRadius } from './mixins/_border-radius.mixin';
import { Hover } from './mixins/_hover.mixin';
import { MediaBreakpointUp } from './mixins/_breakpoints.mixin';

export default $ => css`
  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    height: ${$.cardHeight};
    word-wrap: break-word;
    background-color: ${$.cardBg};
    background-clip: border-box;
    border: ${$.cardBorderWidth} solid ${$.cardBorderColor};
    ${new BorderRadius($.cardBorderRadius).include($)};

    > hr {
      margin-right: 0;
      margin-left: 0;
    }

    > .list-group {
      border-top: inherit;
      border-bottom: inherit;

      &:first-of-type {
        border-top-width: 0;
        ${new BorderTopRadius($.cardInnerBorderRadius).include($)};
      }

      &:last-child {
        border-bottom-width: 0;
        ${new BorderBottomRadius($.cardInnerBorderRadius).include($)};
      }
    }

    > .card-header + .list-group,
    > .list-group + .card-footer {
      border-top: 0;
    }
  }

  .card-body {
    flex: 1 1 auto;
    min-height: 1px;
    padding: ${$.cardSpacerX};
    color: ${$.cardColor};
  }

  .card-title {
    margin-bottom: ${$.cardSpacerY};
  }

  .card-subtitle {
    margin-top: ${math(`-${$.cardSpacerY} * 0.5`)};
    margin-bottom: 0;
  }

  .card-text:last-child {
    margin-bottom: 0;
  }

  .card-link {
    ${new Hover().include(
      $,
      css`
        text-decoration: none;
      `
    )}

    + .card-link {
      margin-left: ${$.cardSpacerX};
    }
  }

  .card-header {
    padding: ${$.cardSpacerY} ${$.cardSpacerX};
    margin-bottom: 0;
    color: ${$.cardCapColor};
    background-color: ${$.cardCapBg};
    border-bottom: ${$.cardBorderWidth} solid ${$.cardBorderColor};

    &:first-of-type {
      ${new BorderRadius(`${$.cardInnerBorderRadius} ${$.cardInnerBorderRadius} 0 0`).include($)};
    }
  }

  .card-footer {
    padding: ${$.cardSpacerY} ${$.cardSpacerX};
    color: ${$.cardCapColor};
    background-color: ${$.cardCapBg};
    border-top: ${$.cardBorderWidth} solid ${$.cardBorderColor};

    &:last-child {
      ${new BorderRadius(`0 0 ${$.cardInnerBorderRadius} ${$.cardInnerBorderRadius}`).include($)};
    }
  }

  .card-header-tabs {
    margin-right: ${math(`-${$.cardSpacerX} * 0.5`)};
    margin-bottom: ${math(`-${$.cardSpacerY}`)};
    margin-left: ${math(`-${$.cardSpacerX} * 0.5`)};
    border-bottom: 0;
  }

  .card-header-pills {
    margin-right: ${math(`-${$.cardSpacerX} * 0.5`)};
    margin-left: ${math(`-${$.cardSpacerX} * 0.5`)};
  }

  .card-img-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding: ${$.cardImgOverlayPadding};
    ${new BorderRadius($.cardInnerBorderRadius).include($)};
  }

  .card-img,
  .card-img-top,
  .card-img-bottom {
    flex-shrink: 0;
    width: 100%;
  }

  .card-img,
  .card-img-top {
    ${new BorderTopRadius($.cardInnerBorderRadius).include($)};
  }

  .card-img,
  .card-img-bottom {
    ${new BorderBottomRadius($.cardInnerBorderRadius).include($)};
  }

  .card-deck {
    .card {
      margin-bottom: ${$.cardDeckMargin};
    }

    ${new MediaBreakpointUp('sm').include(
      $,
      css`
        display: flex;
        flex-flow: row wrap;
        margin-right: ${math(`-${$.cardDeckMargin}`)};
        margin-left: ${math(`-${$.cardDeckMargin}`)};

        .card {
          // Flexbugs #4: https://github.com/philipwalton/flexbugs#flexbug-4
          flex: 1 0 0%;
          margin-right: ${$.cardDeckMargin};
          margin-bottom: 0; // Override the default
          margin-left: ${$.cardDeckMargin};
        }
      `
    )}
  }

  .card-group {
    // The child selector allows nested \`.card\` within \`.card-group\`
    // to display properly.
    > .card {
      margin-bottom: ${$.cardGroupMargin};
    }

    ${new MediaBreakpointUp('sm').include(
      $,
      css`
        display: flex;
        flex-flow: row wrap;
        // The child selector allows nested \`.card\` within \`.card-group\`
        // to display properly.
        > .card {
          // Flexbugs #4: https://github.com/philipwalton/flexbugs#flexbug-4
          flex: 1 0 0%;
          margin-bottom: 0;

          + .card {
            margin-left: 0;
            border-left: 0;
          }

          // Handle rounded corners
          ${$.enableRounded
            ? css`
                &:not(:last-child) {
                  ${new BorderRightRadius(0).include($)};

                  .card-img-top,
                  .card-header {
                    // stylelint-disable-next-line property-disallowed-list
                    border-top-right-radius: 0;
                  }
                  .card-img-bottom,
                  .card-footer {
                    // stylelint-disable-next-line property-disallowed-list
                    border-bottom-right-radius: 0;
                  }
                }

                &:not(:first-of-type) {
                  ${new BorderLeftRadius(0).include($)};

                  .card-img-top,
                  .card-header {
                    // stylelint-disable-next-line property-disallowed-list
                    border-top-left-radius: 0;
                  }
                  .card-img-bottom,
                  .card-footer {
                    // stylelint-disable-next-line property-disallowed-list
                    border-bottom-left-radius: 0;
                  }
                }
              `
            : css``}
        }
      `
    )}
  }

  .card-columns {
    .card {
      margin-bottom: ${$.cardColumnsMargin};
    }

    ${new MediaBreakpointUp('sm').include(
      $,
      css`
        column-count: ${$.cardColumnsCount};
        column-gap: ${$.cardColumnsGap};
        orphans: 1;
        widows: 1;

        .card {
          display: inline-block; // Don't let them vertically span multiple columns
          width: 100%; // Don't let their width change
        }
      `
    )}
  }

  .accordion {
    overflow-anchor: none;

    > .card {
      overflow: hidden;

      &:not(:last-of-type) {
        ${new BorderBottomRadius(0).include($)};
      }

      &:not(:first-of-type) {
        ${new BorderTopRadius(0).include($)};
      }

      > .card-header {
        ${new BorderRadius(0).include($)};
        margin-bottom: ${math(`-${$.cardBorderWidth}`)};
      }
    }
  }
`;
